import { HubConnection, SignalRConnectionInfo, useSignalrConnection } from '@cscglobal-tbs/notifications-ui';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from '@tanstack/react-query';
import { createContext, useCallback, useEffect, useRef } from 'react';
import { useGetSignalRConnectionInformation } from '../api/notifications/notifications';
import { EventKeys } from '../utils/EventGrid.constants';

interface SignalREventHandler<T = any> {
    handler: (data: T) => void | Promise<void>;
    priority: number;
}

export interface INotificationProvider {
    signalRConnectionInfo: SignalRConnectionInfo | undefined;
    isSignalRConnectionInformationErrored: boolean;
    refetchSignalRConnectionInformation: <TPageData>(
        options?: RefetchOptions & RefetchQueryFilters<TPageData>
    ) => Promise<QueryObserverResult<any, any>>;
    connection: HubConnection | null;
    addEventHandler: <T = any>(eventKey: EventKeys, handler: (data: T) => void | Promise<void>, priority: number) => () => void;
}

export const NotificationContext = createContext<INotificationProvider | null>(null);

export function NotificationProvider(props: { value?: INotificationProvider; children?: React.ReactNode }) {
    const {
        data: signalRConnectionInformation,
        isError: isSignalRConnectionInformationErrored,
        refetch: refetchSignalRConnectionInformation,
    } = useGetSignalRConnectionInformation();
    const signalRConnectionInfo: SignalRConnectionInfo | undefined =
        signalRConnectionInformation !== undefined &&
        signalRConnectionInformation.connectionInformation?.accessToken &&
        signalRConnectionInformation.connectionInformation?.url
            ? {
                  accessToken: signalRConnectionInformation?.connectionInformation?.accessToken || '',
                  url: signalRConnectionInformation?.connectionInformation?.url || '',
                  HubConnectionBuilder: HubConnectionBuilder,
                  LogLevel: LogLevel.Error,
              }
            : undefined;

    const connection = useSignalrConnection(signalRConnectionInfo);

    const handlersRef = useRef<Map<string, SignalREventHandler<any>[]>>(new Map());
    const masterHandlersRef = useRef<Map<string, (data: any) => Promise<void>>>(new Map());

    /**
     * Register a handler for a SignalR event with explicit priority.
     * Lower priority numbers execute first (1 = first, 2 = second, etc.).
     */
    const addEventHandler = useCallback(
        (eventKey: EventKeys, handler: (data: any) => void | Promise<void>, priority: number = 1) => {
            if (!connection) {
                return () => {};
            }

            const newHandler: SignalREventHandler<any> = { handler, priority };
            const handlers = handlersRef.current.get(eventKey) || [];
            handlers.push(newHandler);

            // Sort by priority: ASCENDING (lower number = runs first)
            handlers.sort((a, b) => a.priority - b.priority);
            handlersRef.current.set(eventKey, handlers);

            // Register master handler on SignalR (only once per event)
            if (!masterHandlersRef.current.has(eventKey)) {
                const masterHandler = async (data: any) => {
                    const currentHandlers = handlersRef.current.get(eventKey) || [];
                    for (const { handler: h } of currentHandlers) {
                        try {
                            await h(data);
                        } catch (error) {
                            console.error(`Error in SignalR handler for event "${eventKey}":`, error);
                        }
                    }
                };
                connection.on(eventKey, masterHandler);
                masterHandlersRef.current.set(eventKey, masterHandler);
            }

            // Return cleanup function
            return () => {
                const handlers = handlersRef.current.get(eventKey);
                if (handlers) {
                    const index = handlers.indexOf(newHandler);
                    if (index > -1) {
                        handlers.splice(index, 1);
                    }
                }
            };
        },
        [connection]
    );

    // Cleanup all handlers on unmount
    useEffect(() => {
        return () => {
            if (connection) {
                masterHandlersRef.current.forEach((masterHandler, eventKey) => {
                    connection.off(eventKey, masterHandler);
                });
            }
            handlersRef.current.clear();
            masterHandlersRef.current.clear();
        };
    }, [connection]);

    return (
        <NotificationContext.Provider
            value={{
                signalRConnectionInfo,
                isSignalRConnectionInformationErrored,
                refetchSignalRConnectionInformation,
                connection,
                addEventHandler,
            }}
        >
            {props.children}
        </NotificationContext.Provider>
    );
}

export default NotificationProvider;
